import { useCallback, useState } from "react";
import type { AddressDto, CustomerDetailDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { useI18n } from "../../i18n/index.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { MapPin, Plus, Trash2 } from "lucide-react";

interface Form {
  kind: string;
  label: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

const EMPTY_FORM: Form = {
  kind: "site",
  label: "",
  line1: "",
  line2: "",
  city: "",
  region: "",
  postalCode: "",
  country: "",
};

export function AddressSection({
  customer,
  onChanged,
}: {
  customer: CustomerDetailDto;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.addAddress(customer.id, form);
      push(t("customers.addresses.addedToast"));
      setForm(EMPTY_FORM);
      setAdding(false);
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [customer.id, form, onChanged, push, t]);

  const remove = useCallback(
    async (address: AddressDto) => {
      try {
        await api.deleteAddress(customer.id, address.id);
        push(t("customers.addresses.removedToast"));
        onChanged();
      } catch (err) {
        if (err instanceof ApiClientError) push(err.message, undefined, "danger");
      }
    },
    [customer.id, onChanged, push, t]
  );

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">
          {t("customers.addresses.title")}
        </h2>
        <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("customers.addresses.add")}
        </Button>
      </div>

      {customer.addresses.length === 0 && !adding ? (
        <p className="text-sm text-ink-muted">{t("customers.addresses.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {customer.addresses.map((address) => (
            <li
              key={address.id}
              className="flex items-start justify-between gap-2 rounded border border-line px-3 py-2"
            >
              <div className="min-w-0 text-sm">
                <p className="flex items-center gap-2 font-medium text-ink">
                  <MapPin
                    className="h-4 w-4 shrink-0 text-ink-muted"
                    aria-hidden="true"
                  />
                  {address.label || address.kind}
                </p>
                <p className="text-xs text-ink-muted">
                  {[
                    address.line1,
                    address.line2,
                    address.city,
                    address.region,
                    address.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => void remove(address)}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t("common.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="mt-3 space-y-3 border-t border-line pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectInput
              label={t("common.fields.kind")}
              options={[
                { value: "site", label: t("status.address.site") },
                { value: "billing", label: t("status.address.billing") },
                { value: "shipping", label: t("status.address.shipping") },
                { value: "other", label: t("status.address.other") },
              ]}
              value={form.kind}
              onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
            />
            <TextInput
              label={t("common.fields.label")}
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              hint={t("customers.addresses.labelHint")}
            />
          </div>
          <TextInput
            label={t("common.fields.street")}
            required
            value={form.line1}
            onChange={(e) => setForm((f) => ({ ...f, line1: e.target.value }))}
          />
          <TextInput
            label={t("common.fields.line2")}
            value={form.line2}
            onChange={(e) => setForm((f) => ({ ...f, line2: e.target.value }))}
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <TextInput
              label={t("common.fields.city")}
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            />
            <TextInput
              label={t("common.fields.region")}
              value={form.region}
              onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
            />
            <TextInput
              label={t("common.fields.postalCode")}
              value={form.postalCode}
              onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
            />
          </div>
          <TextInput
            label={t("common.fields.country")}
            value={form.country}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={submit}
              disabled={busy || !form.line1.trim()}
            >
              {busy ? t("common.saving") : t("customers.addresses.saved")}
            </Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
